import { Fragment, useEffect, useRef, useState } from 'react';

import Link from 'next/link';
import { useRouter } from 'next/router';

import { XCircleIcon } from '@heroicons/react/24/outline';
import { Dialog } from '@reach/dialog';
import { motion, useScroll } from 'framer-motion';

import {
  CommunityNavGithubLogo,
  Logo,
  MenuIcon,
  NavGithubLogo,
} from '@/components/icons/ElementIcons';
import { GleecAcquisitionModal } from '@/components/shared/GleecAcquisitionModal';
import { KomodoWalletBtnNav } from '@/components/shared/KomodoWalletBtnNav';

import pagesWithMissingNavbar from '@/data/configs/missingNavbar.json';

interface NavItem {
  href: string;
  label: string;
  className?: string;
  // Ghost lives next to this app rather than inside it - /blog/ and /academy/
  // sit beside /komodo/ on the same host. next/link would prefix them with the
  // basePath, so those entries are rendered as plain anchors instead.
  outsideBasePath?: boolean;
}

const NAV_ITEMS: NavItem[] = [
  { href: '/wallet/', label: 'Wallet' },
  { href: '/developer/', label: 'Developer' },
  { href: '/blog/', label: 'News', outsideBasePath: true },
  { href: '/downloads/', label: 'Downloads' },
  { href: '/contact/', label: 'Contact' },
];

// Helper function to determine if a route should be highlighted
const shouldHighlightRoute = (currentPath: string, itemHref: string): boolean => {
  // Remove trailing slashes for comparison
  const normalizedCurrentPath = currentPath.replace(/\/$/, '');
  const normalizedItemHref = itemHref.replace(/\/$/, '');

  // Exact match only
  return normalizedCurrentPath === normalizedItemHref;
};

// Desktop navigation link component
const DesktopNavLink = ({
  href,
  label,
  currentPath,
  outsideBasePath,
}: {
  href: string;
  label: string;
  currentPath: string;
  outsideBasePath?: boolean;
}) => {
  const isHighlighted = shouldHighlightRoute(currentPath, href);
  const className = `mt-1 hidden px-2 py-2 lg:block ${
    isHighlighted
      ? 'nav-highlight-gradient font-bold hover:opacity-80'
      : 'font-medium text-white hover:text-gray-300'
  }`;

  if (outsideBasePath) {
    return (
      <a href={href} className={className}>
        {label}
      </a>
    );
  }

  return (
    <Link href={href} className={className}>
      {label}
    </Link>
  );
};

// Mobile navigation link component
const MobileNavLink = ({
  href,
  label,
  onClick,
  currentPath,
  outsideBasePath,
}: {
  href: string;
  label: string;
  onClick: () => void;
  currentPath: string;
  outsideBasePath?: boolean;
}) => {
  const isHighlighted = shouldHighlightRoute(currentPath, href);
  const className = `px-2 text-lg font-medium ${
    isHighlighted ? 'nav-highlight-gradient' : 'text-white'
  }`;

  if (outsideBasePath) {
    return (
      <li className='pb-4'>
        <a href={href} onClick={onClick} className={className}>
          {label}
        </a>
      </li>
    );
  }

  return (
    <li className='pb-4'>
      <Link href={href} onClick={onClick} className={className}>
        {label}
      </Link>
    </li>
  );
};

// Mobile navigation dialog component
const MobileNavDialog = ({
  isOpen,
  onClose,
  navItems,
  mobileNavRef,
  currentPath,
}: {
  isOpen: boolean;
  onClose: () => void;
  navItems: NavItem[];
  mobileNavRef: React.RefObject<HTMLDivElement>;
  currentPath: string;
}) => (
  <Dialog isOpen={isOpen} onDismiss={onClose}>
    <div className=''>
      <div className='fixed inset-0 bg-black bg-opacity-70' />
      <div className='fixed inset-0'>
        <div className='relative z-[10000] w-full transition-all'>
          <div className='container relative mx-auto max-h-[95%] overflow-y-auto rounded-2xl bg-slate-900 px-4 py-4 shadow-xl transition-all xs:px-8 sm:px-6 md:py-9 lg:py-4'>
            <div
              ref={mobileNavRef}
              className={`transform pb-4 text-left align-middle font-sans text-white transition-all`}
            >
              <div className='flex w-full flex-row items-center justify-between'>
                {/* eslint-disable-next-line @next/next/no-html-link-for-pages -- The site root is outside this app's /komodo basePath. */}
                <a href='/' aria-label='Gleec logo' className='mr-4'>
                  <Logo />
                </a>

                <button
                  type='button'
                  className='text-blue-200 hover:text-blue-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2'
                  onClick={onClose}
                >
                  <XCircleIcon className='h-8 w-8' />
                </button>
              </div>
              <ul className='mt-4 space-y-1'>
                {navItems.map((item, index) => (
                  <MobileNavLink
                    key={index}
                    href={item.href}
                    label={item.label}
                    onClick={onClose}
                    currentPath={currentPath}
                    outsideBasePath={item.outsideBasePath}
                  />
                ))}
                <li className='pb-4'>
                  <Link
                    href='https://github.com/GLEECBTC'
                    target='_blank'
                    className='flex flex-row items-center px-2'
                    onClick={onClose}
                  >
                    <NavGithubLogo className='h-6 w-6' />
                    <span className='ml-2 text-lg font-medium'>Github</span>
                  </Link>
                </li>
                <li className='block lg:hidden' onClick={onClose}>
                  <KomodoWalletBtnNav />
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Dialog>
);

interface SuperNavProps {
  fixed: boolean;
}

export const SuperNav = ({ fixed }: SuperNavProps) => {
  const { scrollYProgress } = useScroll();
  const router = useRouter();
  const isCommunityLp = router.asPath === '/community/';
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const mobileNavRef = useRef<HTMLDivElement>(null);

  const closeMobileNavModal = () => setIsMobileNavOpen(false);
  const openMobileNavModal = () => setIsMobileNavOpen(true);

  useEffect(() => {
    const closeMobileNavModalIfClickOutside = (event: MouseEvent) => {
      if (mobileNavRef.current && !mobileNavRef.current.contains(event.target as Node)) {
        closeMobileNavModal();
      }
    };
    document.addEventListener('mousedown', closeMobileNavModalIfClickOutside);

    return () => {
      document.removeEventListener('mousedown', closeMobileNavModalIfClickOutside);
    };
  }, []);

  // Check if current route should show progress bar
  const shouldShowProgressBar =
    (router.asPath.includes('/blog/') || router.asPath.includes('/academy/')) &&
    !/^\/en\/(blog|academy)\/?$/.test(router.asPath) &&
    !/^\/en\/(blog|academy)\/\d+\/?$/.test(router.asPath) &&
    !router.asPath.includes('/tags/') &&
    !router.asPath.includes('/tag/') &&
    !router.asPath.includes('/authors/') &&
    !router.asPath.includes('/author/');

  const isNavbarHidden = pagesWithMissingNavbar.some((page) => router.asPath.includes(page));

  return (
    <>
      <header
        className={`${
          fixed ? 'fixed' : 'absolute'
        } firefox:bg-opacity-90 top-0 z-[10000] w-full bg-opacity-30 px-4 font-sans text-white backdrop-blur-lg backdrop-filter transition-all xs:px-8 lg:px-0 ${isNavbarHidden ? 'hidden' : ''}`}
        id='nav'
      >
        {shouldShowProgressBar && (
          <motion.div
            className='fixed left-0 right-0 top-0 z-[20000] h-[7px] origin-left bg-[#4986ea] transition-all duration-300'
            style={{ scaleX: scrollYProgress }}
          />
        )}
        <div className='firefox:bg-opacity-90 fake-div-for-tw z-0 z-[7000] mb-4 hidden h-[23px] h-[37px] h-[67px] table-auto space-x-2 border-gray-500 bg-black bg-opacity-90 pt-7'></div>
        <div
          className='container relative mx-auto py-4 transition-all md:py-2'
          id='nav-padding-div'
        >
          <nav className='relative flex items-center justify-between lg:grid lg:grid-cols-24 lg:justify-center lg:gap-0 lg:px-8 xl:px-0'>
            <div className='hidden xl:col-span-2 xl:block'></div>
            <div className='flex w-full md:col-span-5 md:items-center'>
              <div className='flex w-full justify-between md:w-auto'>
                {/* eslint-disable-next-line @next/next/no-html-link-for-pages -- The site root is outside this app's /komodo basePath. */}
                <a href='/' aria-label='Gleec logo' className=''>
                  <Logo />
                </a>
              </div>
            </div>
            <div className='mt-3 hidden items-center py-2 lg:col-span-13 lg:mx-auto lg:mt-0 lg:flex lg:py-0 lg:text-sm xl:col-span-10 xl:text-base 2xl:text-lg'>
              {NAV_ITEMS.map((item, index) => (
                <DesktopNavLink
                  key={index}
                  href={item.href}
                  label={item.label}
                  currentPath={router.asPath}
                  outsideBasePath={item.outsideBasePath}
                />
              ))}
            </div>

            <div className='hidden justify-end md:col-span-4 md:items-center lg:col-span-6 lg:flex xl:col-span-5'>
              <div className='ml-12 flex flex-row items-center'>
                <Link href='https://github.com/GLEECBTC' target='_blank'>
                  {isCommunityLp ? (
                    <CommunityNavGithubLogo className='mr-2 h-8 w-8 duration-300 hover:-translate-y-2' />
                  ) : (
                    <NavGithubLogo className='mr-2 h-8 w-8 duration-300 hover:-translate-y-2' />
                  )}
                </Link>
                <KomodoWalletBtnNav />
              </div>
            </div>

            <div className='hidden xl:col-span-2 xl:block'></div>

            <div className='block lg:hidden'>
              <button
                type='button'
                onClick={openMobileNavModal}
                className='relative'
                aria-label='Open navigation'
              >
                <MenuIcon className='h-9 w-9 stroke-slate-500' />
              </button>
              <MobileNavDialog
                isOpen={isMobileNavOpen}
                onClose={closeMobileNavModal}
                navItems={NAV_ITEMS}
                mobileNavRef={mobileNavRef}
                currentPath={router.asPath}
              />
            </div>
          </nav>
        </div>
      </header>
      <GleecAcquisitionModal />
    </>
  );
};
